import { Theme } from '@mui/material';
import { DATE_PRESET_LABELS, Leg, LegsListItem } from '@/state/legs/legsTypes';
import {
    addDays,
    differenceInDays,
    differenceInMilliseconds,
    format,
    formatDuration,
    intervalToDuration,
    parse,
    set,
    startOfDay,
} from 'date-fns';
import { Balance, BalancesMap } from '@/state/balances/balancesTypes';
import { ConcludedJournalEntry } from '@/state/journals/journalTypes';
import formatNumber, { getCurrencySymbol } from './numberFormatHelpers';
import { BalanceConversion } from '@/hooks/useBalancesConversionById';
import { LOADING_STATE, TimeZone } from '@/state/general/generalTypes';
import { toZonedTime } from 'date-fns-tz';
import { convertSecondsToDuration } from '@/lib/helpers/durationHelpers';
import { DERIVATIVE_POSITION_STATUS, POSITION_DIRECTION } from '@/lib/constants/exchanges';
import { EXCHANGES, SortingTable } from '@/lib/types/generalTypes';
import { GeneralPosition } from '@/lib/websocket/websocketTypes';
import store from '@/store/store';
import syncStoreService from '@/store/syncStore/SyncStoreService';
import { setApiKeyLoading } from '@/state/general/generalSlice';
import { ExchangeAccount } from '@/state/exchanges/exchangesSliceTypes';

export { formatNumber };

/** Formats a date using a date-fns format string; returns the format string itself on parse error. */
export const getDateFormattedDate = (date, formatStr, locale?: Locale) => {
    try {
        return format(new Date(date), formatStr, { locale });
    } catch (e) {
        return formatStr;
    }
};

/** Returns true if `path` equals or contains `url`; safely handles null/undefined inputs. */
export const pathMatcher = (path: string | null | undefined, url: string | undefined): boolean => {
    if (typeof path !== 'string') return false;
    if (typeof url !== 'string') return false;

    if (path === url) return true;
    return path?.includes(url);
};

/** Returns a random integer in the inclusive range [min, max]. */
export const getRandomNumber = (min: number, max: number): number => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
};

/** Formats a number with B/M/K suffixes; optionally prepends a USD or custom currency symbol. */
export const nFormatter = (
    number,
    isUsd = false,
    options: { fraction?: number; currency?: string; round?: boolean } = {},
) => {
    if (typeof number !== 'number') return 'N/A';

    const num = Math.abs(number);
    const sign = number >= 0 ? '' : '-';
    const { fraction, round } = options;
    const getFraction = (numericValue: number): number => {
        const toFormat = Math.abs(numericValue);
        const intPart = `${toFormat}`.split('.');
        const length = intPart[0].length;
        if (length >= 3) return 0;
        return Math.abs(length - 3);
    };

    const formatB = 1000_000_000;
    const formatM = 1000_000;
    const formatK = 1000;
    let finalCurrency = '';
    if (isUsd) {
        finalCurrency = '$';
    }
    if (options?.currency) {
        finalCurrency = getCurrencySymbol(options.currency, 'en-Us');
    }

    if (num >= formatB) {
        const valueToFormat = num / formatB;
        return (
            sign +
            finalCurrency +
            formatNumber(valueToFormat, fraction || getFraction(valueToFormat), null, {
                round,
            }).replace(/\.0$/, '') +
            'B'
        );
    }
    if (num >= formatM) {
        const valueToFormat = num / formatM;
        return (
            sign +
            finalCurrency +
            formatNumber(valueToFormat, fraction || getFraction(valueToFormat), null, {
                round,
            }).replace(/\.0$/, '') +
            'M'
        );
    }
    if (num >= formatK) {
        const valueToFormat = num / formatK;

        return (
            sign +
            finalCurrency +
            formatNumber(valueToFormat, fraction || getFraction(valueToFormat), null, {
                round,
            }).replace(/\.0$/, '') +
            'K'
        );
    }

    return (
        sign +
        finalCurrency +
        formatNumber(num, fraction || getFraction(num), null, { round }).replace(/\.0$/, '')
    );
};

/** Counts legs with positive PnL as wins and legs with negative PnL as losses. */
export const calculateWinsLose = (legs: Leg[]) => {
    const wins = legs.filter((trade) => trade.realisedPnLusd > 0).length;
    const loses = legs.filter((trade) => trade.realisedPnLusd < 0).length;

    return { wins, loses };
};

/** Returns the duration of a single trade leg in seconds (closeDate − openDate). */
export const getLegDurationSec = (leg: Leg | ConcludedJournalEntry) => {
    const start = new Date(leg.openDate).getTime() / 1000;
    const end = new Date(leg.closeDate).getTime() / 1000;
    return end - start;
};

/** Returns the mean duration in seconds across all provided legs; returns 0 for an empty array. */
export const getAvgLegsDurationSeconds = (legs: Leg[] | ConcludedJournalEntry[]): number => {
    const durations = legs.map((leg) => getLegDurationSec(leg));
    return durations.reduce((a, b) => a + b, 0) / durations.length || 0; // returns MS
};

interface BalanceMapTemp {
    [walletType: string]: {
        balances: BalancesMap;
        total: { btc: number };
    };
}

/** Transforms a flat balances structure into a nested map keyed by walletType → symbol with numeric coercions. */
export const getBalancesMap = (balances: {
    [walletType: string]: { balances: Balance[]; total: { btc: number } };
}): BalanceMapTemp => {
    const updatedBalances = { ...balances };
    const result: BalanceMapTemp = {};

    for (const walletType in updatedBalances) {
        const balanceItem: BalancesMap = {};

        if (updatedBalances[walletType]?.balances) {
            updatedBalances[walletType].balances.forEach((balance) => {
                balanceItem[balance.symbol] = {
                    ...balance,
                    value: balance.value ? +balance.value : 0,
                    btcValue: balance.btcValue || 0,
                };
            });
            result[walletType] = {
                balances: balanceItem,
                total: updatedBalances[walletType].total,
            };
        }
    }

    return result as BalanceMapTemp;
};

interface LongShortAnalyticsData {
    totalLength: number;
    sidePercent: number;
    winRatio: number;
    tradesDurationStr: string;
    totalPnl: number;
    avgWinsPnl: number;
    avgLossPnl: number;
    winsAboveAvgRate: number;
    winsBelowAvgRate: number;
    wins: number;
    loses: number;
}

/** Computes win ratio, PnL stats, and duration breakdown for one side (long or short) of the trade analytics. */
export const calculateLongShortAnalytics = (
    data,
    legsLength,
    locale: string,
): LongShortAnalyticsData => {
    const wins = data?.wins?.countLegs || 0;
    const loses = data?.loss?.countLegs || 0;
    const totalLength = data?.countLegs || 0;
    const sidePercent = (totalLength / legsLength) * 100;

    const winRatio = (wins / totalLength) * 100;
    const tradesDurationStr = convertSecondsToDuration(data?.avgDuration, locale);

    const totalPnl = data?.totalRealisedPnL || 0;
    const avgWinsPnl = data?.wins?.avgRealisedPnL || 0;
    const avgLossPnl = data?.loss?.avgRealisedPnL || 0;

    const winsAboveAvgRate =
        (data?.wins?.countAboveAvgDuration * 100) / data?.countAboveAvgDuration || 0;
    const winsBelowAvgRate =
        ((data?.wins?.countLegs - data?.wins?.countAboveAvgDuration) /
            (data?.countLegs - data?.countAboveAvgDuration)) *
            100 || 0;

    return {
        totalLength: data?.countLegs || 0,
        sidePercent,
        winRatio,
        tradesDurationStr,
        totalPnl,
        avgWinsPnl,
        avgLossPnl,
        winsAboveAvgRate,
        winsBelowAvgRate,
        wins,
        loses,
    };
};

/** Computes daily trading statistics (win ratios, long/short counts, PnL) for a single day label. */
export const calculateByDayAnalytics = (
    dayData,
    isUsd: boolean,
    coin: string,
    label: string,
    shortLabel?: string,
) => {
    const totalLength = dayData?.countLegs || 0;
    const winRatio = (dayData?.wins?.countLegs / totalLength) * 100 || 0;
    const shorts = dayData?.sell?.countLegs || 0;
    const longs = dayData?.buy?.countLegs || 0;
    const longWins = dayData?.buy?.wins.countLegs || 0;
    const shortWins = dayData?.sell?.wins.countLegs || 0;
    const longWinRatio = (longWins / longs) * 100 || 0;
    const shortWinRatio = (shortWins / shorts) * 100 || 0;
    const currency = isUsd ? 'USD' : '';

    return {
        label,
        shortLabel: shortLabel ?? label,
        totalLength,
        winRatio,
        longWinRatio,
        shortWinRatio,
        pnl: dayData?.totalRealisedPnL || 0,
        currency,
        isUsd,
        longsLength: dayData?.buy?.countLegs || 0,
        shortsLength: dayData?.sell?.countLegs || 0,
        coin: isUsd ? '' : coin,
    };
};

/** Generates a numeric array from `start` (inclusive) to `stop` (exclusive) with a given `step`. */
export function range(start: number, stop: number, step: number): number[] {
    if ((step > 0 && start >= stop) || (step < 0 && start <= stop)) {
        return [];
    }

    const result: number[] = [];
    for (let i = start; step > 0 ? i < stop : i > stop; i += step) {
        result.push(i);
    }

    return result;
}

/** Determines the minimum decimal places needed to show a significant digit for a number; capped at 12. */
export const findFraction = (
    num: number | string,
    initialFraction: number = 5,
    maxFraction?: number,
): number => {
    if (num === 0 || num === null || num === undefined || isNaN(+num)) return initialFraction;
    const stringValue = Math.abs(+num).toString();
    let fraction = initialFraction;

    const parts = stringValue.split('.');
    const fractional = parts?.[1] || null;
    const isScientific = stringValue.includes('e');

    if (fractional) {
        let index = -1;
        for (let i = 0; i < fractional.length; i++) {
            if (fractional[i] !== '0') {
                index = i;
                break;
            }
        }
        if (index + 1 > initialFraction) {
            fraction = index + 1;
        }
    }
    if (isScientific) {
        const coef = stringValue.split('e')[1];
        const coefValue = Math.abs(Number(coef));
        if (coefValue > 4) {
            fraction = coefValue + 1;
        }
    }

    if (fraction > 12) {
        fraction = 12;
    }

    if (maxFraction && fraction > maxFraction) {
        return maxFraction;
    }

    return fraction;
};

/** Sorts a table data array by the specified column and direction; handles numeric and string columns. */
export const sortTableData = (
    data: any[],
    sortBy: SortingTable,
    disableCaseSensitive?: boolean,
) => {
    const dataForSort = [...data];

    dataForSort.sort((a, b) => {
        const firstValue =
            a && a?.original?.[sortBy.column] !== undefined ? a.original[sortBy.column] : -1;
        const secondValue =
            b && b?.original?.[sortBy.column] !== undefined ? b.original[sortBy.column] : -1;

        if (!isNaN(+firstValue) && !isNaN(+secondValue)) {
            if (+firstValue < +secondValue) {
                return sortBy.direction === 'asc' ? -1 : 1;
            }
            if (+firstValue > +secondValue) {
                return sortBy.direction === 'asc' ? 1 : -1;
            }
            return 0;
        } else {
            let backupFirst = firstValue;
            let backupSecond = secondValue;

            if (disableCaseSensitive) {
                backupFirst = firstValue ? firstValue.toString().toLowerCase() : '';
                backupSecond = secondValue ? secondValue.toString().toLowerCase() : '';
            }

            if (backupFirst < backupSecond) {
                return sortBy.direction === 'asc' ? -1 : 1;
            }
            if (backupFirst > backupSecond) {
                return sortBy.direction === 'asc' ? 1 : -1;
            }
            return 0;
        }
    });

    return dataForSort;
};

/** Returns false when `label` is a "last N trades" preset, indicating no real date range is active. */
export const isDateAvailable = (label: string | null | undefined): boolean => {
    if (!label) return true;

    return !(
        label === DATE_PRESET_LABELS.LAST_10_TRADES ||
        label === DATE_PRESET_LABELS.LAST_50_TRADES ||
        label === DATE_PRESET_LABELS.LAST_25_TRADES ||
        label === DATE_PRESET_LABELS.LAST_100_TRADES ||
        label === DATE_PRESET_LABELS.LAST_250_TRADES ||
        label === DATE_PRESET_LABELS.LAST_500_TRADES
    );
};

/** Returns the absolute day difference between two dates as a ceiling integer; returns -1 if either date is falsy. */
export const getDateDifferenceInDays = (date1: Date | string, date2: Date | string) => {
    if (!date1 || !date2) return -1;
    const diffTime = Math.abs(+new Date(date2) - +new Date(date1));
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/** Extracts a structured error object (message, statusCode, errorCode, etc.) from an Axios error response. */
export const extractError = (error: any) => {
    const message = {
        message: '', // error message
        statusCode: null,
        error: '', // title
        reason: '',
        errorCode: '',
    };

    message.message = error?.response?.data?.message || 'Something went wrong';
    message.statusCode = error?.response?.data?.statusCode || null;
    message.error = error?.response?.data?.error || 'Error';
    message.reason = error?.response?.data?.reason || '';
    message.errorCode = error?.response?.data?.errorCode || '';

    return message;
};

/** Generates a random alphanumeric string of the given length. */
export const randomString = (length: number): string => {
    let text = '';
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    for (let i = 0; i < length; i++) {
        text += possible.charAt(Math.floor(Math.random() * possible.length));
    }

    return text;
};

/** Safe JSON.parse wrapper that returns `valueOnReject` (default null) instead of throwing on invalid input. */
export const jsonParser = (json: string, valueOnReject: any = null) => {
    try {
        return json ? JSON.parse(json) : valueOnReject;
    } catch (e) {
        return valueOnReject;
    }
};

/** Sums the `returnKey` field from balance items whose walletType matches `filterKey`. */
export const getFilteredBalanceSum = (
    array: BalanceConversion[],
    filterKey: string,
    returnKey: string,
) => {
    return array.reduce((acc, curr) => {
        const wallet = curr?.wallet || curr?.walletType || '';
        if (wallet === filterKey) {
            return acc + curr[returnKey];
        } else {
            return acc;
        }
    }, 0);
};

/** Splits an array into sub-arrays of at most `chunkSize` elements. */
export const sliceIntoChunks = (arr: any[], chunkSize: number): any[][] => {
    const res: any[][] = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
        const chunk: any[] = arr.slice(i, i + chunkSize);
        res.push(chunk);
    }
    return res;
};

/** Groups legs by close date into a map keyed by the date string formatted with `keyFormat`. */
export const groupLegsByDate = (
    legs: LegsListItem,
    keyFormat: string = 'yyyy-MM-dd',
): Record<string, Leg[]> => {
    const items: Record<string, Leg[]> = {};
    legs?.items?.forEach((leg) => {
        const key = format(new Date(leg.closeDate), keyFormat);
        items[key] = [...(items[key] || []), leg];
    });
    return items;
};

/** Formats an ISO date string as a human-readable elapsed string (e.g. "2 hours ago"); uses i18next `t` for the "ago" label. */
export const formatDurationString = (
    update: string,
    t: any,
    locale?: any,
    disablePostfix?: boolean,
): string => {
    const now = new Date();
    let startEnd = {
        start: new Date(update),
        end: now,
    };

    if (disablePostfix) {
        startEnd = {
            start: now,
            end: new Date(update),
        };
    }

    const interval = intervalToDuration(startEnd);
    if (!interval) return 'N/A';

    let durationFormatter = ['minutes'];
    // @ts-ignore
    if (interval?.months > 0) {
        durationFormatter = ['months', 'days', 'hours', 'minutes'];
        // @ts-ignore
    } else if (interval?.days > 0) {
        durationFormatter = ['days', 'hours', 'minutes'];
        // @ts-ignore
    } else if (interval?.hours > 0) {
        durationFormatter = ['hours', 'minutes'];
        // @ts-ignore
    } else if (interval?.minutes > 0) {
        durationFormatter = ['minutes'];
        // @ts-ignore
    } else if (interval?.seconds > 0) {
        durationFormatter = ['seconds'];
    }

    const formattedDuration = formatDuration(interval, { format: durationFormatter, locale });
    return formattedDuration
        ? `${formattedDuration} ${!disablePostfix ? t('duration:ago') : ''}`
        : t('duration:now');
};

/** Converts a `TimeZone[]` array into sorted, labeled dropdown options with UTC offset display (e.g. "(UTC +05:30) Kolkata"). */
export const getLabeledTimezones = (
    timezones: TimeZone[],
): {
    label: string;
    value: string;
    offset: number;
}[] => {
    const labeled = timezones.map((timezone) => {
        const hour = Math.abs(parseInt(`${timezone.offset / 60}`, 10));
        const minute = Math.abs(timezone.offset % 60);
        let sign = timezone.offset < 0 ? '+' : '-';
        if (timezone.offset === 0) {
            sign = '';
        }

        const hourString = hour < 10 ? `0${hour}` : `${hour}`;
        const minuteString = minute < 10 ? `0${minute}` : `${minute}`;

        const label = `(UTC ${sign}${hourString}:${minuteString}) ${timezone.name}`;
        return {
            label,
            value: timezone.name,
            offset: timezone.offset,
        };
    });

    labeled.sort((a, b) => {
        return a.label.localeCompare(b.label);
    });

    return labeled;
};

/** Converts a UTC date to the equivalent wall-clock time in the specified IANA timezone. */
export const convertDateToSelectedTimezone = (date: Date, timezone: string): Date => {
    return toZonedTime(new Date(new Date(date).toUTCString()), timezone || '');
};

/** Returns a shallow copy of an object with all properties whose value is an empty string removed. */
export const removeEmptyStringValues = (obj) => {
    const result = {};
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            if (obj[key] !== '') {
                result[key] = obj[key];
            }
        }
    }

    return result;
};

/** Returns the Date if it is a valid Date instance, otherwise returns 0. */
export const isValidDate = (date) => {
    // @ts-ignore
    return date instanceof Date && !isNaN(date) ? date : 0;
};

/** Parses a `yyyy-MM-dd` string as local midnight, avoiding UTC timezone offset issues. */
export const createFixedDate = (dateStr: string): Date => {
    if (!dateStr) return new Date();
    // Parse the string as if it is in the local timezone
    let date = parse(dateStr, 'yyyy-MM-dd', new Date());
    // Ensure the time is exactly midnight in local time
    date = set(date, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });

    return date;
};

/** Formats a date string as `D/Mon/YY HH:mm` (e.g. "5/Jun/25 14:30"). */
export const formatDate = (dateString) => {
    const date = new Date(dateString);

    const day = date.getDate();
    const month = date.toLocaleString('en-GB', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/** Normalizes a `GeneralPosition[]` from the WebSocket into the internal position shape stored in TinyBase. */
export const getTransformedPositions = (positions: GeneralPosition[], apiKeyId: number) =>
    positions.map((position) => {
        const amount = position?.amount ? +position.amount : 0;
        let side: any = position.direction;

        if (side === 'sell') {
            side = POSITION_DIRECTION.SHORT;
        }

        if (side === 'buy') {
            side = POSITION_DIRECTION.LONG;
        }

        if (side === POSITION_DIRECTION.BOTH) {
            side = amount >= 0 ? POSITION_DIRECTION.LONG : POSITION_DIRECTION.SHORT;
        }

        const isUsdt = position.exchangeId === EXCHANGES.WOO ? true : Boolean(position?.isUsdt);
        let conversionId = `${position.symbol?.toLowerCase()}-usd-${position.exchangeId?.toLowerCase()}`;
        if (
            position.exchangeId === EXCHANGES.BINANCE &&
            position.symbol?.toLowerCase() === 'ethbtc'
        ) {
            conversionId = `eth-btc-${position.exchangeId?.toLowerCase()}`;
        }
        return {
            ...position,
            status: DERIVATIVE_POSITION_STATUS.OPENED,
            lastUpdated: position?.lastUpdated || new Date().toISOString(),
            side: side as POSITION_DIRECTION,
            direction: side as POSITION_DIRECTION,
            contractType: position?.contractType || null,
            symbolId: position?.symbolId || undefined,
            isUsdt,
            conversionId,
            amount,
            accountId: `${apiKeyId}`,
            entryPrice: +position.entryPrice,
            liquidationPrice: +position?.liquidationPrice || null,
            pnl: position?.pnl ? Number(position.pnl) : 0,
            created: position?.created ?? '',
        };
    });

/** Groups positions by account ID, transforms each via `getTransformedPositions`, and writes results to TinyBase. */
export const buildPositionsTable = (data: GeneralPosition[]) => {
    const positionsByApiId = data.reduce((acc: Record<string, GeneralPosition[]>, position) => {
        const { accountId } = position;
        if (!acc[accountId]) {
            acc[accountId] = [];
        }
        acc[accountId].push(position);
        return acc;
    }, {});

    return Object.entries(positionsByApiId).forEach(([accountId, positions]) => {
        const transformed: any[] = getTransformedPositions(positions, +accountId);
        syncStoreService.setPositionsRowsInTransaction(transformed);
        store.dispatch(
            setApiKeyLoading({
                apiKey: +accountId,
                loadingKey: 'positions',
                status: LOADING_STATE.SUCCESS,
            }),
        );
    });
};

/** Converts a camelCase, spaced, or hyphenated string to snake_case. */
export const toSnakeCase = (text: string): string => {
    return text
        .replace(/([a-z])([A-Z])/g, '$1_$2')
        .replace(/\s+/g, '_')
        .replace(/-+/g, '_')
        .toLowerCase();
};

/** Returns a 0–100 liquidation proximity value; 0 means safe, 100 means at the liquidation price. */
export const calculateProgress = (
    entry: number,
    live: number,
    liquidation: number,
    direction: 'long' | 'short',
) => {
    if ((direction === 'short' && live < entry) || (direction === 'long' && live > entry)) {
        return 0;
    }

    let progress = 0;

    if (direction === 'short') {
        progress = ((live - entry) / (liquidation - entry)) * 100;
    } else {
        progress = ((entry - live) / (entry - liquidation)) * 100;
    }

    return Math.max(0, Math.min(100, progress));
};

/** Truncates a string to `start` characters + "..." + the last `end` characters. */
export const shortenString = (str: string, start = 6, end = 3): string => {
    if (str?.length > start - 3) {
        return str?.substring(0, start) + '...' + str?.substring(str?.length - end);
    }
    return str;
};

/** Truncates a display name to `symbols` characters followed by a period (default: 7 chars). */
export const shortenDisplayName = (str: string, symbols = 7): string => {
    if (str?.length > 7) {
        return str?.substring(0, symbols) + '.';
    }
    return str;
};

/** Returns "+" for positive values and "" for zero or negative (used as N-formatter sign prefix). */
export const getSignForNFormatted = (value) => {
    return value > 0 ? '+' : '';
};

/** Returns "+", "-", or "" for positive, negative, or zero values respectively. */
export const getSignForFormattedNumber = (value) => {
    if (value === 0) return '';
    return value > 0 ? '+' : '-';
};

/** Returns MUI color token and sign character for a value that changed relative to a past value. */
export const getFormatNumberStyle = (valueNow: number, valuePast: number) => {
    if (valueNow > valuePast) return { sign: '+', color: 'success.main' };
    if (valueNow < valuePast) return { sign: '-', color: 'error.main' };
    return { sign: '', color: 'text.primary' };
};

/** Like `getFormatNumberStyle` but uses an empty sign for negative values (for N-formatted display). */
export const getNFormatterStyle = (valueNow: number, valuePast: number) => {
    if (valueNow > valuePast) return { sign: '+', color: 'success.main' };
    if (valueNow < valuePast) return { sign: '', color: 'error.main' };
    return { sign: '', color: 'text.primary' };
};

/** Returns a MUI color token (success/error/text.primary) and sign character for a signed number value. */
export const getColoredTextOptions = (value: number, neutralColor?) => {
    if (isNaN(value)) {
        return {
            color: 'text.primary',
            sign: '',
        };
    }

    if (value > 0) {
        return {
            color: 'success.main',
            sign: '+',
        };
    }
    if (value < 0) {
        return {
            color: 'error.main',
            sign: '-',
        };
    }
    return {
        color: neutralColor ? neutralColor : 'text.primary',
        sign: '',
    };
};

/** Like `getColoredTextOptions` but resolves to raw theme primitive hex colors instead of MUI token strings. */
export const getPrimitivesColoredTextOptions = (value: number, theme: Theme) => {
    if (isNaN(value)) {
        return {
            color: theme.palette.text.primary,
            sign: '',
        };
    }

    if (value > 0) {
        return {
            color: theme.palette.primitives.shamrock['500'],
            sign: '+',
        };
    }
    if (value < 0) {
        return {
            color: theme.palette.primitives.thunderbird['500'],
            sign: '-',
        };
    }
    return {
        color: theme.palette.customText.text1,
        sign: '',
        text: 'N/A',
    };
};

/** Returns color/sign based on position direction (LONG=green/+, SHORT=red/-); falls back to value-based coloring for BOTH. */
export const getPositionValueStyle = (
    value: number,
    side: POSITION_DIRECTION,
    neutralColor?: string,
) => {
    if (side === POSITION_DIRECTION.SHORT) {
        return {
            color: 'error.main',
            sign: '-',
        };
    }

    if (side === POSITION_DIRECTION.LONG) {
        return {
            color: 'success.main',
            sign: '+',
        };
    }

    return getColoredTextOptions(value, neutralColor);
};

/** Like `getPositionValueStyle` but returns raw theme primitive hex colors instead of MUI token strings. */
export const getPrimitivesPositionValueStyle = (
    value: number,
    side: POSITION_DIRECTION,
    theme: Theme,
) => {
    if (side === POSITION_DIRECTION.SHORT) {
        return {
            color: theme.palette.primitives.thunderbird['500'],
            sign: '-',
        };
    }

    if (side === POSITION_DIRECTION.LONG) {
        return {
            color: theme.palette.primitives.shamrock['500'],
            sign: '+',
        };
    }

    return getPrimitivesColoredTextOptions(value, theme);
};

/** Calculates display decimal places for a HyperLiquid price given its mid price, size decimals, and max decimals. */
export const getDigits = (midPx: string, szDecimals: number, maxDecimals: number): number => {
    if (midPx === undefined || midPx === null) {
        return 0;
    }
    const significantDigits = 5;
    if (Number(midPx) === 0) {
        if (szDecimals >= maxDecimals) {
            return Math.min(significantDigits, maxDecimals);
        } else {
            return Math.min(significantDigits, maxDecimals - szDecimals);
        }
    } else if (Number(midPx) < 1) {
        let precision: number;

        if (szDecimals >= maxDecimals) {
            precision = Math.min(significantDigits, maxDecimals);
        } else {
            precision = Math.min(significantDigits, maxDecimals - szDecimals);
        }

        const pricePrecized = Number(midPx).toPrecision(precision);
        const [_, decimalPart = ''] = pricePrecized.toString().split('.');
        return Math.min(maxDecimals - szDecimals, decimalPart.length);
    } else {
        let precision: number;

        if (szDecimals >= maxDecimals) {
            precision = Math.max(significantDigits, maxDecimals);
        } else {
            precision = Math.max(significantDigits, maxDecimals - szDecimals);
        }

        const pricePrecized = Number(midPx).toPrecision(precision);
        const [integerPart, decimalPart = ''] = pricePrecized.toString().split('.');
        // tslint:disable-next-line:variable-name
        const _significantDigits = Math.max(significantDigits, integerPart.length);
        return Math.min(maxDecimals - szDecimals, _significantDigits - integerPart.length);
    }
};

/** Calculates allowed price decimal places from symbol price, amount precision, and exchange max decimals. */
export function calculateSymbolPrecision(
    price: number,
    amountPrecision: number,
    maxDecimals: number = 6,
): number {
    let pricePrecision = 0;

    // Handle edge cases
    if (price === undefined || price === null || amountPrecision === undefined) {
        return 0;
    }

    // Convert price to string to analyze its decimal structure
    const priceStr = price.toString();
    const priceSplitted = priceStr.split('.');

    if (priceStr === '0' || parseFloat(priceStr) === 0) {
        // Significant digits is always 5 in this case
        const significantDigits = 5;
        // Integer digits is always 0 in this case (0 doesn't count)
        const integerDigits = 0;
        // Calculate the price precision
        pricePrecision = Math.min(maxDecimals - amountPrecision, significantDigits - integerDigits);
    } else if (parseFloat(priceStr) > 0 && parseFloat(priceStr) < 1) {
        // Significant digits, always 5 in this case
        const significantDigits = 5;
        // Get the part after the decimal separator
        const decimalPart = priceSplitted[1] || '';
        // Count the number of leading zeros in the decimal part
        let leadingZeros = 0;
        while (leadingZeros <= decimalPart.length && decimalPart[leadingZeros] === '0') {
            leadingZeros = leadingZeros + 1;
        }
        // Calculate price precision based on leading zeros and significant digits
        pricePrecision = leadingZeros + significantDigits;
        // Calculate the price precision based on maxDecimals - szDecimals and the calculated price precision from the previous step
        pricePrecision = Math.min(maxDecimals - amountPrecision, pricePrecision);
    } else {
        // Count the numbers before the decimal separator
        const integerPart = priceSplitted[0] || '';
        // Get significant digits, take the max() of 5 and the integer digits count
        const significantDigits = Math.max(5, integerPart.length);
        // Calculate price precision based on maxDecimals - szDecimals and significantDigits - integerPart.length
        pricePrecision = Math.min(
            maxDecimals - amountPrecision,
            significantDigits - integerPart.length,
        );
    }

    return Math.max(0, Math.floor(pricePrecision));
}

/** Returns whole days remaining until a millisecond expiry timestamp; returns 0 if expired or undefined. */
export const getDaysLeft = (expiresAtMs: number | undefined) => {
    if (!expiresAtMs) return 0;

    const today = startOfDay(new Date());
    const targetDate = startOfDay(new Date(expiresAtMs));

    const diff = differenceInDays(targetDate, today) - 1;

    return diff > 0 ? diff : 0;
};

/** Returns whole hours remaining until local midnight, based on the browser's local time/timezone. */
export const getHoursUntilTomorrow = (): number => {
    const now = new Date();
    const nextMidnight = addDays(startOfDay(now), 1);

    return Math.ceil(differenceInMilliseconds(nextMidnight, now) / (1000 * 60 * 60));
};

/** Maps a dollar volume value to a visual fill size integer (4–16) for glow/intensity effects. */
export const getGlowingFillSize = (value: number) => {
    if (value <= 100) {
        return 4;
    } else if (value > 100 && value <= 500) {
        return 5;
    } else if (value > 500 && value <= 1000) {
        return 6;
    } else if (value > 1000 && value <= 5000) {
        return 7;
    } else if (value > 5000 && value <= 10000) {
        return 8;
    } else if (value > 10000 && value <= 50000) {
        return 9;
    } else if (value > 50000 && value <= 100000) {
        return 10;
    } else if (value > 100000 && value <= 250000) {
        return 11;
    } else if (value > 250000 && value <= 500000) {
        return 12;
    } else if (value > 500000 && value <= 1_000_000) {
        return 13;
    } else if (value > 1_000_000 && value <= 2_000_000) {
        return 14;
    } else if (value > 2_000_000 && value <= 5_000_000) {
        return 15;
    } else if (value > 5_000_000) {
        return 16;
    }
};

/** Returns the appropriate decimal places for a price based on magnitude: 0 for tiny, 8 for sub-1, 4 for 1–10, 2 for ≥10. */
export const getDecimals = (value: number, skipFirstStep = false) => {
    const absValue = Math.abs(value);

    if (absValue < 0.01 && !skipFirstStep) {
        if (absValue === 0 || isNaN(value)) {
            return 0;
        }

        const exponent = Math.floor(Math.log10(absValue));
        return Math.max(0, 2 - exponent - 1);
    } else if (absValue < 1) {
        return 8;
    } else if (absValue < 10) {
        return 4;
    } else {
        return 2;
    }
};

/** Returns true if `accountId` is the lowest-ID (oldest) account for its exchange across all accounts. */
export const findIsOldestAccount = (accountId: number, allAccounts: ExchangeAccount[]) => {
    const account = allAccounts.find((item) => item.id === accountId);
    if (!account) return false;

    const theOldestFromCurrentExchange = allAccounts
        .filter((item) => item.exchangeId === account?.exchangeId)
        .map((item) => item.id)
        .toSorted((a, b) => +a - +b)
        .shift();

    return accountId === theOldestFromCurrentExchange;
};
